/**
 * The portions worth a chip, for one food and one person.
 *
 * The pad used to offer the food's default serving and three round numbers,
 * which is a guess at how people eat. Nobody eats 30, 100 or 150 g of oats;
 * they eat 45 g because that is what the scoop holds, and having typed 4-5
 * once they should not have to type it again. So the row is built from three
 * sources, in an order that is also a ranking of how much each one knows:
 *
 * 1. **What the product says a portion is.** Every serving the food carries —
 *    the packet's "30 g", Open Food Facts' "1 portion (330 ml)", the whole
 *    pack where the pack is one sitting, the "1 portion" typed into the manual
 *    form. These are facts about the product rather than about the plate, and
 *    they stay on the row for as long as the food exists.
 * 2. **What this person actually weighs.** The weights they have logged this
 *    food at, most often first — `Portion`, from the server, per user.
 * 3. **The round numbers**, to fill the row for a food nobody has weighed yet.
 *
 * Deduplicated, because "100 g" offered twice is a chip that does nothing and
 * a chip that does nothing is a chip you have to read. The first source to
 * claim a weight keeps it, so a habitual 30 g reads as the packet's portion
 * rather than as a coincidence. Capped, because past half a dozen the row
 * scrolls off the side of a phone and the seventh weight is quicker to type.
 *
 * Shared rather than written into each pad because both clients have one, and
 * a chip that logs 45 g on the web and 50 g on the phone for the same habit is
 * a bug rather than a platform difference.
 */

import type { FoodItem, Portion } from "./types";

/** Where a chip's number came from — which is also what it looks like. */
export type PortionKind = "serving" | "used" | "round";

export interface PortionChip {
  /** What the chip says. Short: it shares a row with five others. */
  label: string;
  grams: number;
  kind: PortionKind;
  /** The same thing in a sentence, for assistive tech and tooltips. */
  detail: string;
}

/** The round numbers people actually weigh to, in basis units. */
export const ROUND_PORTIONS: readonly number[] = [30, 100, 150];

/** How many chips fit a row before the next is quicker to type than to find. */
export const MAX_CHIPS = 6;

/** How many habitual weights the row takes before the round numbers fill it. */
export const MAX_USED = 3;

/**
 * A weight as a chip prints it: whole where it is whole, one decimal where
 * somebody typed one. `Math.round` alone turned a 12.5 g biscuit into a chip
 * that logged 13 g of it.
 */
export function formatGrams(grams: number): string {
  const tenths = Math.round(grams * 10) / 10;
  return Number.isInteger(tenths) ? String(tenths) : tenths.toFixed(1);
}

/** Labels that are nothing but the weight again: "30 g", "330ml", "1 x 25 g". */
const BARE_QUANTITY = /^(?:\d+\s*[x×]\s*)?\d+(?:[.,]\d+)?\s*(?:g|ml)$/i;

/** How long a serving's word may run before it eats the row. */
const WORD_MAX = 20;

/**
 * The words on a serving, without the weight the chip is about to print.
 *
 * Sources disagree about what a serving is called. The packet says "30 g",
 * Open Food Facts says "1 portion (330 ml)" or "2 biscuits (25g)", the label
 * reader says "1 serving" and the manual form says "1 portion". The chip says
 * the weight itself, so a label that is only the weight would print it twice;
 * that one becomes "1 portion", and the rest lose their bracketed repeat.
 */
export function servingWord(label: string): string {
  const word = label
    .replace(/\([^)]*\)/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!word || BARE_QUANTITY.test(word)) return "1 portion";
  return word.length > WORD_MAX ? `${word.slice(0, WORD_MAX - 1).trimEnd()}…` : word;
}

/**
 * The chips for one food, given what this person has logged it at.
 *
 * `used` is the server's answer, already ordered most-habitual-first, and may
 * be absent — the request is in flight, or the food has never been weighed —
 * in which case the row is the packet's portions and the round numbers, which
 * is what it always was.
 */
export function suggestedPortions(
  food: Pick<FoodItem, "basis_unit" | "servings">,
  used: readonly Portion[] = [],
): PortionChip[] {
  const unit = food.basis_unit;
  const chips: PortionChip[] = [];
  // Keyed at the precision the chip prints, so 45 and 45.04 are one chip.
  const seen = new Set<number>();

  const take = (grams: number, kind: PortionKind, label: string, detail: string): boolean => {
    if (!(grams > 0)) return false;
    const key = Math.round(grams * 10);
    if (seen.has(key)) return false;
    seen.add(key);
    chips.push({ label, grams: key / 10, kind, detail });
    return true;
  };

  // The product's own portions, the default first because it is the one the
  // pad opens on and the eye should find it where the number is.
  const servings = [...food.servings].sort((a, b) => Number(b.is_default) - Number(a.is_default));
  for (const serving of servings) {
    const word = servingWord(serving.label);
    const figure = `${formatGrams(serving.grams)} ${unit}`;
    take(serving.grams, "serving", `${word} · ${figure}`, `${word}, ${figure}, from the product`);
  }

  let habits = 0;
  for (const portion of used) {
    if (habits >= MAX_USED) break;
    const figure = `${formatGrams(portion.grams)} ${unit}`;
    const times = portion.times === 1 ? "used once" : `used ${portion.times} times`;
    if (take(portion.grams, "used", figure, `${figure}, ${times}`)) habits += 1;
  }

  for (const grams of ROUND_PORTIONS) {
    take(grams, "round", `${grams} ${unit}`, `${grams} ${unit}`);
  }

  return chips.slice(0, MAX_CHIPS);
}
